import { Direction } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CardDesignComponent } from 'src/app/@shared/components/design/slide-up-card/card-design.component';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { LocalStorageService } from 'src/app/@shared/services/local-storage/local-storage.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { RouteFiltersComponent } from 'src/app/modules/route-planner/components/route-planner-form/forms/source-destination-form/route-filters/route-filters.component';
import { SourceDestinationDetailedResultComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/source-destination/source-destination-detailed-result/source-destination-detailed-result.component';
import { TimeInformationComponent } from 'src/app/modules/route-planner/components/route-planner-results/shared/pop-ups/time-information/time-information.component';
import { SourceDestinationResultsMapComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/source-destination/source-destination-results-map/source-destination-results-map.component';
import { eDepartureTimeOptions } from 'src/app/modules/route-planner/constants/departure-time-options.constants';
import {
  AVAILABLE_ROUTES,
  FILTER,
  ROUTE_PLANNING,
  SHARE,
  SHOW_ON_MAP,
  ROUTE_DETAILS,
  DEPARTURE_AT,
  ARRIVAL_AT,
  FROM,
  TO_DIRECTION,
  ADD_TO_FAVORITES,
  MORE_ACTIONS_MENU,
  PRINT,
  PRINT_ARIA_LABEL,
  REMOVE_FROM_FAVORITES,
  TIMES_LEGEND,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { RoutePlanning } from 'src/app/modules/route-planner/interfaces/route-planning/@route-planning.interface';
import { SourceDestinationForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/source-destination-form';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { SourceDestinationRouteTrackingComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/source-destination/source-destination-route-tracking/source-destination-route-tracking.component';
import { FilterStateService } from 'src/app/modules/route-planner/services/filters/filter-state.service';
import { eTransportationOptions } from 'src/app/modules/route-planner/constants/transportation-options.constants';
import { HourPipe } from 'src/app/modules/route-planner/pipes/hour.pipe';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { eFavoriteType } from 'src/app/modules/route-planner/enums/favorite/favorite-type.enum';
import { FavoriteRoute } from 'src/app/modules/route-planner/interfaces/favorites/favorite-route.interface';
import { PrintService } from 'src/app/modules/route-planner/services/print/print.service';

@Component({
    selector: 'app-source-destination-results-container',
    templateUrl: './source-destination-results-container.component.html',
    styleUrls: ['./source-destination-results-container.component.scss'],
    standalone: false
})
export class SourceDestinationResultsContainerComponent
  extends CardDesignComponent
  implements OnInit, AfterViewInit {
  @ViewChild('card') override card;

  formValues: SourceDestinationForm;
  results: RoutePlanning[];
  resubmittedWithoutFilters: boolean;
  leg: Address | null
  appliedFilters: boolean;
  isLoading: boolean = false;
  isFavoriteRoute: boolean;
  moreActionsMenuItems: SelectOption<string>[] = [];

  routePlanningText: string;
  filterText: string;
  shareRouteText: string;
  availableRoutesText: string;
  showOnMapText: string;
  printText: string;
  printAriaLabelText: string;
  timeLegendText: string;
  moreActionsMenuText: string;
  addToFavoritesText: string;
  removeFromFavoritesText: string;

  direction: Direction;

  isFirstTime: boolean = true;

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,
    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: { appliedFilters: boolean },
    private readonly modalService: ModalService,
    private readonly localStorageService: LocalStorageService,
    private readonly languageService: LanguageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly filterStateService: FilterStateService,
    private readonly favoritesService: FavoritesService,
    private readonly printService: PrintService
  ) {
    super(routePlanningService, resizeService, overlay);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.routePlanningService._formSubject.subscribe((routePlanning) => {
      this.results = this.applyCustomFilter(routePlanning.results);
      this.formValues = routePlanning.formValues as SourceDestinationForm;
      this.resubmittedWithoutFilters = routePlanning.resubmittedWithoutFilters;
      this.leg = routePlanning.leg

      // Check if current route is favorite
      this.checkIfFavoriteRoute();

      // Check for routeId in URL and select the matching route if present
      this.checkForSharedRoute();
    });

    // Subscribe to loading state changes
    this.routePlanningService.getLoadingState().subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    // Subscribe to route tracking - should work on all devices
    // But only open overlay on mobile devices
    this.routePlanningService._trackingRouteSub.subscribe((route) => {
      if (!this.isDesktop) {
        // Use setTimeout to ensure cardElement is available after ViewChild initialization
        setTimeout(() => {
          if (this.card?.cardElement) {
            this.openComponentOverlay(
              this.direction,
              SourceDestinationRouteTrackingComponent,
              route,
              this.card.cardElement
            );
          } else {
            console.warn('[ROUTE_TRACKING] Card element not available');
          }
        }, 0);
      }
    });

    const currentFilters = this.filterStateService.getCurrentFilters();
    this.appliedFilters =
      !this.filterStateService.filtersAreDefault(currentFilters);

    this.initializeMultiLanguageTexts();
  }

  ngAfterViewInit(): void {
    if (!!this.isDesktop) {
      this.routePlanningService._mapOverlaySub.next({
        mapComponent: SourceDestinationResultsMapComponent,
        data: {
          routesToDisplay: this.results,
        },
      });
    }

    // Check for shared route after view is initialized
    const routeId = this.activatedRoute.snapshot.queryParams['routeId'];
    if (routeId && this.results && this.results.length > 0) {
      console.debug('Processing shared route after view init');
      // Small delay to ensure everything is ready
      //  setTimeout(() => this.checkForSharedRoute(), 500);
    }
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      if(this.direction) {
        this.handleClose(true);
        return;
      }
      this.direction = language.direction;
      this.routePlanningText = ROUTE_PLANNING[language.language];
      this.filterText = FILTER[language.language];
      this.availableRoutesText = AVAILABLE_ROUTES[language.language];
      this.shareRouteText = SHARE[language.language];
      this.showOnMapText = SHOW_ON_MAP[language.language];

      this.printText = PRINT[language.language]
      this.printAriaLabelText = PRINT_ARIA_LABEL[language.language]
      this.timeLegendText = TIMES_LEGEND[language.language]
      this.moreActionsMenuText = MORE_ACTIONS_MENU[language.language]
      this.addToFavoritesText = ADD_TO_FAVORITES[language.language]
      this.removeFromFavoritesText = REMOVE_FROM_FAVORITES[language.language]
      this.buildMoreActionsMenuItems();

      if (!this.isFirstTime) {
        this.manageResultsOnLanguageChange();
      }

      if (this.isFirstTime) {
        this.isFirstTime = false;
      }
    });
  }

  manageResultsOnLanguageChange() {
    if (!this.isLoadingData) {
      this.getRoutePlanningObs(this.formValues, this.formValues.timeOpt);
    }
  }

  private getRoutePlanningObs(
    routePlanningFormValues: SourceDestinationForm,
    selectedTimeOpt: any
  ) {
    this.isLoadingData = true;
    const language = this.routePlanningService.getLanguageForRoute();

    // Retrieve filters from the FilterStateService
    const currentFilters = this.filterStateService.getCurrentFilters();

    // Merge all options from the filter service so that any updates (like transportations) are included.
    const mergedOptions = {
      ...routePlanningFormValues.options,
      ...currentFilters, // override with the current filter values (including isAccessible and transportations)
      // In case the currentFilters.transportations is empty, you can fallback:
      transportations:
        currentFilters.transportations &&
          currentFilters.transportations.length > 0
          ? currentFilters.transportations
          : this.routePlanningService.setTransportations(
            routePlanningFormValues
          ),
      transportationsForInitialUse: routePlanningFormValues.options.transportations,
      arrivalTime: this.routePlanningService.getArrival(
        selectedTimeOpt,
        routePlanningFormValues
      ),
      departureTime: this.routePlanningService.getDeparture(
        selectedTimeOpt,
        routePlanningFormValues
      ),
    };

    return this.routePlanningService
      .getRoutesFromApi({
        route: {
          origin: routePlanningFormValues.origin,
          destination: routePlanningFormValues.destination,
          stop: routePlanningFormValues.stop,
          options: {
            ...routePlanningFormValues.options,
            transportations: this.routePlanningService.setTransportations(
              routePlanningFormValues
            ),
            transportationsForInitialUse: routePlanningFormValues.options.transportations,
            arrivalTime: this.routePlanningService.getArrival(
              selectedTimeOpt,
              routePlanningFormValues
            ),
            departureTime: this.routePlanningService.getDeparture(
              selectedTimeOpt,
              routePlanningFormValues
            ),
          },
        },
        returnedRouteOptions: {
          language,
          isFutureSearch: false
        },
      })
      .subscribe((results) => {
        this.results = results.data;
        this.isLoadingData = false;
      });
  }

  handleResultClick(result: { route: RoutePlanning; showRoutePrice: boolean }) {
    console.debug('Handling result click for route:', result.route);

    // Update the selected route in the service
    this.routePlanningService._resultSubject.next({
      type: 'selected',
      value: result.route,
    });

    // First clear any existing routeId parameter
    this.routePlanningService.clearRouteIdentifier();

    // Then add the new route identifier to the URL for sharing
    const routeIdentifier = this.createRouteIdentifier(result.route);
    this.routePlanningService.addRouteIdentifierToUrl(routeIdentifier);

    console.debug('Opening detailed view for route');
    this.openComponentOverlay(
      this.direction,
      SourceDestinationDetailedResultComponent,
      {
        route: result.route,
        formValues: this.formValues,
        showRoutePrice: result.showRoutePrice,
        leg: this.leg
      },
      this.card.cardElement,
      (returnValue: any) => {
        console.debug('Detailed view closed, clearing selected route');
        this.routePlanningService._resultSubject.next({
          type: 'selected',
          value: null,
        });

        if (returnValue?.favoriteChanged) {
          console.debug('Favorite status changed, updating...');
          this.checkIfFavoriteRoute();
        }
      }
    );
  }

  handleResultHover(route: RoutePlanning) {
    this.routePlanningService._resultSubject.next({
      type: 'highlighted',
      value: route,
    });
  }

  shareResults() {
    // Get the form values from the service
    let formValues: SourceDestinationForm = null;
    this.routePlanningService._formSubject
      .subscribe((formData) => {
        if (formData && formData.formValues) {
          formValues = formData.formValues as SourceDestinationForm;
        }
      })
      .unsubscribe();

    // Set the form parameters in the URL
    if (formValues) {
      // Set origin parameters
      if (formValues.origin) {
        this.routePlanningService.addAddressesToQueryParam([
          {
            queryParam: 'org',
            value: formValues.origin.addressDetails.label,
          },
          {
            queryParam: 'oll',
            value: `${formValues.origin.coordinates.lat}_${formValues.origin.coordinates.lng}`,
          },
        ]);
      }

      // Set destination parameters
      if (formValues.destination) {
        this.routePlanningService.addAddressesToQueryParam([
          {
            queryParam: 'dest',
            value: formValues.destination.addressDetails.label,
          },
          {
            queryParam: 'dll',
            value: `${formValues.destination.coordinates.lat}_${formValues.destination.coordinates.lng}`,
          },
        ]);
      }

      // Set stop parameters if available
      if (formValues.stop) {
        this.routePlanningService.addAddressesToQueryParam([
          {
            queryParam: 'stop',
            value: formValues.stop.addressDetails.label,
          },
          {
            queryParam: 'sll',
            value: `${formValues.stop.coordinates.lat}_${formValues.stop.coordinates.lng}`,
          },
        ]);
      }
    }

    // Check if there's a selected route
    let selectedRoute: RoutePlanning = null;
    this.routePlanningService._resultSubject
      .subscribe((result) => {
        if (result && result.type === 'selected' && result.value) {
          selectedRoute = result.value;
        }
      })
      .unsubscribe();

    if (selectedRoute) {
      // Create a route identifier for the selected route
      const routeIdentifier = this.createRouteIdentifier(selectedRoute);
      // Add the route identifier to the URL
      this.routePlanningService.addRouteIdentifierToUrl(routeIdentifier);
    }

    // Add a small delay to ensure URL is updated before copying
    setTimeout(() => {
      // Copy the current URL to clipboard
      const currentUrl = window.location.href;
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          console.debug('URL copied to clipboard:', currentUrl);
        })
        .catch((err) => {
          console.error('Could not copy URL to clipboard:', err);
        });
    }, 300);

    // Open the share modal with the updated URL
    this.modalService.openShareLinkModal();
  }

  /**
   * Creates a unique identifier for a route based on stable properties
   */
  private createRouteIdentifier(route: RoutePlanning): string {
    // Extract stable properties from the route
    const originLat = route.sections[0].departure.place.location.lat.toFixed(6);
    const originLng = route.sections[0].departure.place.location.lng.toFixed(6);
    const destLat =
      route.sections[
        route.sections.length - 1
      ].arrival.place.location.lat.toFixed(6);
    const destLng =
      route.sections[
        route.sections.length - 1
      ].arrival.place.location.lng.toFixed(6);
    const departureTime = new Date(route.sections[0].departure.time).getTime();
    const arrivalTime = new Date(
      route.sections[route.sections.length - 1].arrival.time
    ).getTime();
    const duration = route.travelSummary.duration;

    // Create a string with a consistent format
    const routeString = `${originLat},${originLng}|${destLat},${destLng}|${departureTime}|${arrivalTime}|${duration}`;

    // Return a base64 encoded version of the string to make it URL-friendly
    return btoa(routeString);
  }

  openFiltersMenu() {
    const savedFiltersRaw = this.localStorageService.getItem('filters');
    const savedFilters =
      typeof savedFiltersRaw === 'string'
        ? JSON.parse(savedFiltersRaw)
        : savedFiltersRaw || {};
    // Reverse the merge order so that savedFilters override formValues.options
    // Use the in-memory filters as the initial filters instead of merging local storage.
    const chosenFilters = this.filterStateService.getCurrentFilters();
    const currentTransportationValues = [...chosenFilters.transportations]


    this.openComponentOverlay(
      this.direction,
      RouteFiltersComponent,
      { initialFilters: chosenFilters },
      this.card.cardElement,

      (returnedValue) => {
        if (returnedValue && returnedValue.value && returnedValue.applyFilters) {
          // Update the form values with the filters received from the overlay
          this.formValues.options = {
            ...this.formValues.options,
            ...returnedValue.value,
          };


          // Preserve the routeId parameter if it exists
          this.preserveRouteIdParameter();
        } else {
          // When closing without applying, update formValues.options with current filters from FilterStateService
          // This ensures that if filters were reset, the formValues reflect the reset state
          const currentFilters = this.filterStateService.getCurrentFilters();
          this.formValues.options = {
            ...this.formValues.options,
            ...currentFilters,
            transportations: [...currentTransportationValues]
          };
        }
        if (returnedValue.applyFilters) {
          this.handleClose(true)
        }
      }
    );
  }

  /**
   * Preserves the routeId parameter when navigating or applying filters
   */
  private preserveRouteIdParameter(): void {
    const routeId = this.activatedRoute.snapshot.queryParams['routeId'];
    if (routeId) {
      // Ensure the routeId parameter is preserved in the URL
      this.routePlanningService.addRouteIdentifierToUrl(routeId);
    }
  }

  handleClose(reSubmitForm: boolean = false) {
    this.routePlanningService._mapOverlaySub.next(null);

    // Clear the routeId parameter when closing the results container
    this.routePlanningService.clearRouteIdentifier();

    if (this.favoritesService.favorites) {
      this.favoritesService.onFavoritesChange$.next(this.favoritesService.favorites);
    }

    this.closeCard(reSubmitForm);
  }

  override handleOverlayEscape(): void {
    this.handleClose(false);
  }

  // Custom filter that checks if we need to apply filtering based on selected transportation options
  private applyCustomFilter(routes: RoutePlanning[]): RoutePlanning[] {
    const currentFilters = this.filterStateService.getCurrentFilters();

    // Check if any of the specified transportation options are included in current filters
    const hasSpecifiedTransportations =
      currentFilters.transportations &&
      (currentFilters.transportations.includes(
        eTransportationOptions.RAHBALIT
      ) ||
        currentFilters.transportations.includes(
          eTransportationOptions.CARMELIT
        ) ||
        currentFilters.transportations.includes(
          eTransportationOptions.LIGHT_TRAIN
        ));

    // If none of the specified transportation options are selected, return all routes without filtering
    if (!hasSpecifiedTransportations) {
      return routes;
    }

    return routes.filter((route) => {
      if (
        !currentFilters.transportations.includes(
          eTransportationOptions.CARMELIT
        )
      ) {
        return !route.sections.some(
          (section) => section.agency?.name === 'כרמלית'
        ); //&&
        // !route.sections.some(section => section.agency?.id === "24266_f5ca38f");
        // section.transport.category === "Cable car"
      }
      if (
        !currentFilters.transportations.includes(
          eTransportationOptions.RAHBALIT
        )
      ) {
        return !route.sections.some(
          (section) => section.agency?.name === 'כבל אקספרס'
        ); //&&
        // !route.sections.some(section => section.agency?.id === "24266_c6f3ac5");
        // section.transport.category === "Cable car"
      }
      if (
        !currentFilters.transportations.includes(
          eTransportationOptions.LIGHT_TRAIN
        )
      ) {
        return !route.sections.some(
          (section) => section.agency?.name === 'תבל'
        ); //&&
        // !route.sections.some(section => section.agency?.id === "24266_785f3ec");
        // section.transport.category === "Tram, Streetcar, Light rail"
      }
      return true;
    });
  }
  handlePrint() {
    const addressesDetails = document.querySelector<HTMLElement>('.print-addresses-details');
    const resultsList = document.querySelector<HTMLElement>('.print-results-list');

    if (!addressesDetails || !resultsList) {
      console.warn('Printable content not found!');
      return;
    }

    const currentLanguage = this.languageService.getSelectedLanguage().language;
    this.printService.print([addressesDetails, resultsList], {
      title: AVAILABLE_ROUTES[currentLanguage],
    });
  }

  /**
   * Checks if a specific route was shared via URL and selects it
   */
  private checkForSharedRoute(): void {
    if (!this.results || this.results.length === 0) {
      console.debug('No results available yet, will retry in 500ms');
      // If results aren't available yet, retry after a short delay
      setTimeout(() => this.checkForSharedRoute(), 500);
      return;
    }

    const routeId = this.activatedRoute.snapshot.queryParams['routeId'];
    if (!routeId) {
      return;
    }

    console.debug('Found routeId in URL, attempting to find matching route');

    try {
      // Find the matching route using the identifier
      const matchingRoute = this.findRouteByIdentifier(routeId);

      if (matchingRoute) {
        console.debug('Found matching route, opening detailed view');

        // First, select the route in the service
        this.routePlanningService.selectRoute(matchingRoute);

        // Then, after a delay to ensure the UI is updated, open the detailed view
        setTimeout(() => {
          this.handleResultClick({
            route: matchingRoute,
            showRoutePrice: false,
          });
        }, 1000);
      } else {
        console.debug('No matching route found for routeId:', routeId);
        // If we couldn't find a match, try again after a delay
        // This handles cases where the results might still be loading
        if (this.isLoading) {
          console.debug('Results are still loading, will retry');
          setTimeout(() => this.checkForSharedRoute(), 1000);
        }
      }
    } catch (error) {
      console.error('Error finding shared route:', error);
    }
  }

  /**
   * Finds a route by its identifier
   * @param routeId The route identifier from the URL
   * @returns The matching route or null if not found
   */
  private findRouteByIdentifier(routeId: string): RoutePlanning | null {
    try {
      // Decode the route identifier
      const routeString = atob(routeId);
      console.debug('Decoded route string:', routeString);

      // Parse the route string
      const parts = routeString.split('|');
      console.debug('Route string parts:', parts);

      if (parts.length >= 5) {
        // Format: originLat,originLng|destLat,destLng|departureTime|arrivalTime|duration
        const originLat = Number(parts[0].split(',')[0]);
        const originLng = Number(parts[0].split(',')[1]);
        const destLat = Number(parts[1].split(',')[0]);
        const destLng = Number(parts[1].split(',')[1]);
        const departureTime = Number(parts[2]);
        const arrivalTime = Number(parts[3]);
        const durationValue = Number(parts[4]);

        return this.findMatchingRoute(
          originLat,
          originLng,
          destLat,
          destLng,
          departureTime,
          arrivalTime,
          durationValue
        );
      } else {
        console.error('Invalid route string format:', routeString);
        return null;
      }
    } catch (error) {
      console.error('Error decoding route identifier:', error);
      return null;
    }
  }

  /**
   * Finds a route matching the given parameters
   */
  private findMatchingRoute(
    originLat: number,
    originLng: number,
    destLat: number,
    destLng: number,
    departureTime: number,
    arrivalTime: number,
    durationValue: number
  ): RoutePlanning | null {
    console.debug('Looking for route with parameters:', {
      originLat,
      originLng,
      destLat,
      destLng,
      departureTime,
      arrivalTime,
      durationValue,
    });

    // First try to find an exact match
    let matchingRoute = this.findExactMatch(
      originLat,
      originLng,
      destLat,
      destLng,
      durationValue
    );

    // If no exact match, try with more tolerance
    if (!matchingRoute) {
      console.debug('No exact match found, trying with more tolerance');
      matchingRoute = this.findApproximateMatch(
        originLat,
        originLng,
        destLat,
        destLng
      );
    }

    return matchingRoute;
  }

  /**
   * Tries to find an exact match for the route
   */
  private findExactMatch(
    originLat: number,
    originLng: number,
    destLat: number,
    destLng: number,
    durationValue: number
  ): RoutePlanning | null {
    return this.results.find((route) => {
      const routeOriginLat = route.sections[0].departure.place.location.lat;
      const routeOriginLng = route.sections[0].departure.place.location.lng;
      const routeDestLat =
        route.sections[route.sections.length - 1].arrival.place.location.lat;
      const routeDestLng =
        route.sections[route.sections.length - 1].arrival.place.location.lng;
      const routeDuration = route.travelSummary.duration;

      // Compare coordinates with small tolerance
      const latLngMatch =
        Math.abs(routeOriginLat - originLat) < 0.0001 &&
        Math.abs(routeOriginLng - originLng) < 0.0001 &&
        Math.abs(routeDestLat - destLat) < 0.0001 &&
        Math.abs(routeDestLng - destLng) < 0.0001;

      // Compare duration with small tolerance
      const durationMatch = Math.abs(routeDuration - durationValue) < 60; // 60 seconds tolerance

      return latLngMatch && durationMatch;
    });
  }

  /**
   * Tries to find an approximate match for the route with more tolerance
   */
  private findApproximateMatch(
    originLat: number,
    originLng: number,
    destLat: number,
    destLng: number
  ): RoutePlanning | null {
    // If we can't find an exact match, just match on origin/destination coordinates with more tolerance
    return this.results.find((route) => {
      const routeOriginLat = route.sections[0].departure.place.location.lat;
      const routeOriginLng = route.sections[0].departure.place.location.lng;
      const routeDestLat =
        route.sections[route.sections.length - 1].arrival.place.location.lat;
      const routeDestLng =
        route.sections[route.sections.length - 1].arrival.place.location.lng;

      // Compare coordinates with larger tolerance
      return (
        Math.abs(routeOriginLat - originLat) < 0.01 &&
        Math.abs(routeOriginLng - originLng) < 0.01 &&
        Math.abs(routeDestLat - destLat) < 0.01 &&
        Math.abs(routeDestLng - destLng) < 0.01
      );
    });
  }

  /**
   * Checks if the current route is in favorites
   */
  private checkIfFavoriteRoute(): void {
    if (!this.formValues) {
      this.isFavoriteRoute = false;
      return;
    }

    const favoriteRoutes = this.favoritesService.favorites?.routes;

    this.isFavoriteRoute = !!favoriteRoutes?.find((route) => {
      const favoriteRouteFormValues = route.formValues;

      return (
        favoriteRouteFormValues.origin.addressDetails.label ==
          this.formValues.origin.addressDetails.label &&
        favoriteRouteFormValues.destination.addressDetails.label ==
          this.formValues.destination.addressDetails.label
      );
    });
  }

  /**
   * Toggles the favorite status of the current route
   */
  toggleFavoriteRoute(): void {
    const favoriteRoute: FavoriteRoute = {
      formValues: this.formValues,
      route: this.results?.[0] || null,
      type: eFavoriteType.ROUTES,
    };

    if (!!this.isFavoriteRoute) {
      this.favoritesService.removeFavorite(favoriteRoute);
    } else {
      this.favoritesService.addNewFavorite(favoriteRoute);
    }

    this.isFavoriteRoute = !this.isFavoriteRoute;
    this.buildMoreActionsMenuItems();
  }

  private buildMoreActionsMenuItems(): void {
    const items: SelectOption<string>[] = [
      {
        label: this.isFavoriteRoute ? this.removeFromFavoritesText : this.addToFavoritesText,
        value: 'favorite',
        icon: this.isFavoriteRoute ? 'heart-filled' : 'heart',
        ariaLabel: this.isFavoriteRoute ? this.removeFromFavoritesText : this.addToFavoritesText,
        onClick: () => this.toggleFavoriteRoute(),
      },
      {
        label: this.printText,
        value: 'print',
        icon: 'print',
        ariaLabel: this.printAriaLabelText,
        onClick: () => this.handlePrint(),
      },
      {
        label: this.timeLegendText,
        value: 'timeInfo',
        icon: 'info',
        ariaHaspopup: 'dialog',
        onClick: () =>
          this.modalService.openPopUp(TimeInformationComponent, {
            hasBackdrop: true,
          }),
      },
    ];

    this.moreActionsMenuItems = items;
  }
}
